'use client';

import type { SidebarPanelContent } from '@/atoms/sidebarPanelAtoms';
import {
  MenuCoachmarkPinPersistStep,
  MenuCoachmarkPinStep,
} from '@/components/layout/Navigation/coachmark';
import { EvidencePanelAction } from '@/components/layout/Navigation/sidebar-panel/panels/evidence/evidence-panel-action';
import { SessionsPanelAction } from '@/components/layout/Navigation/sidebar-panel/panels/sessions/sessions-panel-action';
import { Button } from '@heidi/design-system/button';
import { Kbd } from '@heidi/design-system/kbd';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { isMac } from '@heidi/utils';
import { memo } from 'react';
import { Key } from 'ts-key-enum';

interface PanelPinIconProps {
  className?: string;
  isPinned: boolean;
}

/** Panel pin icon with inner bar that toggles between pinned/unpinned states */
function PanelPinIcon({ className, isPinned }: PanelPinIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Outer border */}
      <rect width="18" height="18" x="3" y="3" rx="3" />
      {/* Inner bar — wide when pinned, narrow when unpinned */}
      <rect
        x="6"
        y="6"
        width={isPinned ? 6 : 2}
        height="12"
        rx="1.5"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

export interface PanelHeaderProps {
  /** Current content type being displayed */
  content: SidebarPanelContent;
  /** Whether the sidebar is pinned */
  isPinned: boolean;
  /** Callback to toggle pin state */
  onTogglePin: () => void;
  /** Whether to show the pin/unpin toggle (hidden on pages without panel content) */
  showPinToggle: boolean;
}

/**
 * Header for the sidebar panel.
 * Shows context-aware "Create" action on the left and pin/unpin toggle on the right.
 */
export const PanelHeader = memo(function PanelHeader({
  content,
  isPinned,
  onTogglePin,
  showPinToggle,
}: PanelHeaderProps) {
  const { t } = useTranslations();

  const pinLabel = isPinned ? t('navigation.unpinPanel') : t('navigation.pinPanel');

  const pinDescription = isPinned
    ? t('navigation.unpinPanelDescription')
    : t('navigation.pinPanelDescription');

  return (
    <div className="shrink-0 pb-2 pt-4">
      <div className="flex h-9 items-center justify-between">
        {/* Left: Context-aware action button */}
        <div className="flex items-center">
          {content === 'sessions' && <SessionsPanelAction />}
          {content === 'evidence' && <EvidencePanelAction />}
        </div>

        {/* Right: Pin/Unpin toggle — hidden on pages without panel content */}
        {showPinToggle && (
          <MenuCoachmarkPinPersistStep>
            <MenuCoachmarkPinStep>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="panel_ghost"
                    size="sm"
                    iconOnly
                    onClick={onTogglePin}
                    aria-label={pinLabel}
                    aria-pressed={isPinned}
                  >
                    <PanelPinIcon isPinned={isPinned} className="!size-4 shrink-0" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  align="start"
                  className="flex flex-col gap-0.5 px-2.5 py-1.5"
                >
                  <div className="flex items-center">
                    <TypographyCaption className="text-text-primary-on-dark">
                      {pinLabel}
                    </TypographyCaption>
                    <div className="ms-2 flex items-center gap-0.5">
                      <Kbd
                        keyType="modifier"
                        isMac={isMac}
                        size="compact"
                        className="border-text-primary-on-dark/20 bg-text-primary-on-dark/10"
                        contentClassName="text-text-primary-on-dark/60"
                      >
                        {Key.Meta}
                      </Kbd>
                      <Kbd
                        size="compact"
                        className="border-text-primary-on-dark/20 bg-text-primary-on-dark/10"
                        contentClassName="text-text-primary-on-dark/60"
                      >
                        /
                      </Kbd>
                    </div>
                  </div>
                  <TypographyCaption className="text-text-primary-on-dark/60">
                    {pinDescription}
                  </TypographyCaption>
                </TooltipContent>
              </Tooltip>
            </MenuCoachmarkPinStep>
          </MenuCoachmarkPinPersistStep>
        )}
      </div>
    </div>
  );
});
